import { Children, isValidElement } from 'react';
import type { Props as AvatarProps } from './Avatar';
import styles from './AvatarsWithText.module.scss';

export type Props = {
  /**
   * `aria-label` attribute for better screenreader text.
   */
  'aria-label'?: string;
  /**
   * Generally a rendered `Avatar`, `AvatarWithTooltip`, or `AvatarGroup` component.
   * Behaves like a Web Component `slot`.
   */
  avatars: React.ReactNode;
  /**
   * Text children to render beside the `Avatar`s.
   * Should generally be `AvatarText` components.
   */
  children: React.ReactNode;
  /**
   * Whether to text across multiple lines.
   */
  multiline?: boolean;
  /**
   * The `size` of the `Avatar`s that render within the group.
   */
  size: AvatarProps['size'];
};

/**
 * Text node to render as `children` within `AvatarsWithText`.
 * 🚨 Only meant to be used within `AvatarsWithText`.
 */
export const AvatarText = (props: {
  'aria-label'?: string;
  children: React.ReactNode;
  kind: 'heavy' | 'light';
}) => {
  return (
    <span
      aria-label={props['aria-label']}
      className={styles.text}
      data-kind={props.kind}
      data-zds
      // `role="text"` isn't technically valid (yet), but it allows
      // VoiceOver to read the content more clearly and prevents it
      // from splitting text. Axe also complains about this, but will
      // stop when the extension uses `axe-core@4.2.0`. See issue:
      // https://github.com/dequelabs/axe-core/issues/1597
      // eslint-disable-next-line jsx-a11y/aria-role
      role="text"
    >
      {props.children}
    </span>
  );
};

/**
 * Displays a group of `Avatar`s with informational text beside them.
 */
export const AvatarsWithText = (props: Props) => {
  // Get the count of the number of children of the props.avatars
  // Helps determine whether or not there should be a grid gap between
  // the avatars and the text
  const childArray = Children.toArray(props.avatars);
  const childCount = isValidElement(childArray[0])
    ? childArray[0].props?.children?.length
    : 0;
  const noGap = childCount > 3 && props.size !== 'large';

  return (
    <div
      aria-label={props['aria-label']}
      className={styles.avatarsWithText}
      data-no-gap={noGap || undefined}
      data-multiline={props.multiline ? '' : undefined}
      data-size={props.size}
      data-zds
    >
      <div className={styles.avatars} data-zds>
        {props.avatars}
      </div>
      <div className={styles.content} data-zds>
        {props.children}
      </div>
    </div>
  );
};
